import React, { ReactNode } from 'react';

import { S3ImageWrapper } from '@/components/shared/S3ImageWrapper/S3ImageWrapper';
import { classNames } from '@/helpers/classNames';
import { useAuth } from '@/providers/AuthProvider';
import { useTeam } from '@/providers/TeamProvider/TeamProvider';
import {
	CircularProgress,
	Avatar as MuiAvatar,
	SvgIcon,
} from '@material-ui/core';
import { isNil } from 'ramda';

import UserAvatar, { AvatarProps, AvatarSize } from './UserAvatar';
import avatarStyles from './UserAvatar.module.scss';
import styles from './UserAvatarGroup.module.scss';

export type AvatarGroupProps = {
	size: AvatarSize;
	className?: string;
	userIds: string[];
	userAvatarProps: Omit<AvatarProps, 'userId'>;
} & Pick<AvatarProps, 'hasNotification' | 'notificationSize' | 'activeSize'>;

export const UserAvatarGroup: React.FC<AvatarGroupProps> = ({
	userAvatarProps,
	size,
	hasNotification,
	notificationSize,
	userIds,
	className,
	activeSize = 'small',
}) => {
	const { user: authUser } = useAuth();
	const {
		team: { members },
	} = useTeam();

	const users = members.filter((m) => userIds.includes(m.user.id));
	const isActive = users
		.filter((u) => u.user.id !== authUser.id)
		.some((u) => u.user.isActive);

	return (
		<div
			className={classNames([
				styles['root'],
				avatarStyles['root'],
				avatarStyles[`size-${size}`],
				className,
			])}
		>
			{userIds.map((uid) => (
				<UserAvatar
					key={`user-avatar-${uid}`}
					userId={uid}
					{...userAvatarProps}
					showActiveStatus={false}
				/>
			))}
			{hasNotification && (
				<div
					className={classNames([
						avatarStyles['notification'],
						avatarStyles[`size-${notificationSize}`],
					])}
				/>
			)}
			{isActive && (
				<div
					className={classNames([
						avatarStyles['active'],
						avatarStyles[`size-${activeSize}`],
					])}
				/>
			)}
		</div>
	);
};

export default UserAvatarGroup;
